import React, { useEffect, useState, useRef } from 'react';
import CustomTooltip from './CustomTooltip';

interface TruncatedTextProps extends React.HTMLAttributes<HTMLElement> {
  text: string | React.ReactNode;
  className?: string;
  tooltipTitle?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'bottom-start' | 'left-start' | 'right-start';
  /**
   * If true, tooltip will always show regardless of truncation.
   * If false (default), tooltip only shows when text is truncated.
   */
  alwaysShowTooltip?: boolean;
  /**
   * HTML element type to render. Defaults to 'span'.
   */
  as?: keyof JSX.IntrinsicElements;
}

/**
 * A reusable component that displays text and shows a tooltip only when the text is truncated.
 * Can be configured to always show tooltip if needed.
 */
const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  className = '',
  tooltipTitle,
  placement = 'top-start',
  alwaysShowTooltip = false,
  as: Component = 'span',
  style,
  onClick,
  ...restProps
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const checkTruncation = () => {
      // If element is not laid out yet, try again soon
      if (!el || el.clientWidth === 0) {
        return;
      }
      const truncated = el.scrollWidth > el.clientWidth;
      setIsTruncated(truncated);
    };

    const rafId = requestAnimationFrame(checkTruncation);
    const t1 = setTimeout(checkTruncation, 50);
    const t2 = setTimeout(checkTruncation, 150);
    const t3 = setTimeout(checkTruncation, 300);

    // Recalculate on window changes
    window.addEventListener('resize', checkTruncation);
    window.addEventListener('orientationchange', checkTruncation);

    let ro: ResizeObserver | null = null;
    try {
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => checkTruncation());
        ro.observe(el);
        if (el.parentElement) {
          ro.observe(el.parentElement);
        }
      }
    } catch (_e) {
    }

    let mo: MutationObserver | null = null;
    try {
      if (typeof MutationObserver !== 'undefined') {
        mo = new MutationObserver(() => checkTruncation());
        mo.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
        if (el.parentElement) {
          mo.observe(el.parentElement, { attributes: true, attributeFilter: ['style', 'class'] });
        }
      }
    } catch (_e) {
      // ignore
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener('resize', checkTruncation);
      window.removeEventListener('orientationchange', checkTruncation);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
    };
  }, [text, className, style]);

  const displayText = tooltipTitle || text;
  const shouldShowTooltip = alwaysShowTooltip || isTruncated;

  const ElementComponent = Component as React.ElementType;
  const element = React.createElement(
    ElementComponent,
    {
      ref: textRef as any,
      className,
      style,
      onClick,
      ...restProps
    },
    text,
  );



  return shouldShowTooltip ? (
    <CustomTooltip title={displayText} placement={placement}>
      {element}
    </CustomTooltip>
  ) : (
    element
  );
};

export default TruncatedText;

